import React from 'react';
import PropTypes from 'prop-types';

import { Grid, Button, Typography, Paper, Link } from '@mui/material';
import { withTheme } from '@mui/styles';

import { Close, Info, Cached } from '@mui/icons-material';

import RequestBuilder from './RequestBuilder';
import Popup from 'reactjs-popup';
import CompactTextField from './CompactTextField';
import { getFixSpecs, verifyHost } from './utils';

import FixRequestBuilder from './fix/FixRequestBuilder';
import {SERVICE_NAMES} from './constants';

// https://developer.mozilla.org/en-US/docs/Web/API/setTimeout#maximum_delay_value;
const MAX_ALLOWED_TIMEOUT = 2147483647;

const info = {
	padding: 10,
	margin: 8,
	border: '1px solid transparent',
	borderRadius: 4,
	fontSize: 14,
	backgroundColor: 'dimgrey',
	borderColor: 'darkgray',
	color: 'gainsboro'
};

function filterMethods(apiSpecs, allowedMethods) {
	const { paths } = apiSpecs;
	return Object.keys(paths).reduce(
		(acc, method) =>
			allowedMethods.includes(method)
				? { ...acc, [method]: paths[method] }
				: acc
		, {});
}

class RequestControl extends React.PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			deribitApi: null,
			currencies: null,
			fixSpecs: null,
			instruments: [],

			serverUrl: props.serverUrl,

			tokenExpired: false,
		};

		this.fetchInstruments();
		this.tokenExpirationTimeout = null;
	}

	onApiFetched(api) {
		if (!this.props.isFix) {
			this.requestBuilder.init();
		}
		if (this.props.service && this.props.service.allowedMethods) {
			api.paths = filterMethods(api, this.props.service.allowedMethods);
		}
	}

	componentDidMount() {
		this.fetchApi().then(api => this.onApiFetched(api));
	}

	componentDidUpdate(prevProps) {
		if (prevProps.service !== this.props.service) {
			this.fetchApi().then(api => this.onApiFetched(api));
		}
	}

	setServerUrl(serverUrl) {
		this.setState({ serverUrl });
	}

	setTokenExpirationTimeout(expiresIn) {
		if (this.tokenExpirationTimeout) {
			clearTimeout(this.tokenExpirationTimeout);
			this.tokenExpirationTimeout = null;
		}
		this.setState({tokenExpired: false});
		const expiryInMs = expiresIn * 1000;

		if (expiryInMs < MAX_ALLOWED_TIMEOUT) {
			this.tokenExpirationTimeout = setTimeout(() => {
				this.setState({tokenExpired: true});
				this.tokenExpirationTimeout = null;
			}, expiryInMs);
		}
	}

	async fetchApi() {
		if (this.props.isFix) {
			const apiXml = await fetch(this.props.fixApiPath);
			const fixSpecs = getFixSpecs(await apiXml.text());

			return this.setState({fixSpecs});
		}

		return fetch(this.props.service.apiPath)
			.then((response) => response.json())
			.then((api) => {
				this.setState({ deribitApi: api });
				return api;
			});
	}

	async fetchInstruments() {
		if (this.state.instruments.length > 0) {
			this.setState({ instruments: [] });
		}
		return fetch(this.props.platformUrl + '/api/v2/public/get_currencies')
			.then((response) => response.json())
			.then((currenciesFull) => {
				if (currenciesFull.result) {
					let currencies = currenciesFull.result.map(currency => currency.currency);
					this.setState({ currencies: currencies });
					return currencies;
				}
				return [];
			}).then(async currencies => {
				return Promise.all(currencies.map(currency =>
					fetch(this.props.platformUrl + '/api/v2/public/get_instruments?expired=false&include_spots=true&currency=' + currency)
						.then((response) => response.json())
						.then((instruments) => {
							return instruments.result.map(instrument => instrument.instrument_name);
						})
				)).then(instruments => {
					let mergedInstruments = instruments.flat().sort().filter((x, i, a) => !i || x != a[i-1]);
					this.setState({ instruments: mergedInstruments });
					return mergedInstruments;
				});
			});
	}

	renderSettings(onClose) {
		return (
			<>
				<Close className="icon-button" style={{ position: 'absolute', top: 8, right: 8, cursor: 'pointer' }} onClick={onClose} />
				<Grid container spacing={2}>
					<Grid item>
						<Typography variant="h6">
							Settings
						</Typography>
					</Grid>
					<Grid container item direction="row" justifyContent="flex-end" alignItems="flex-start" spacing={2}>
						<Grid item xs>
							<CompactTextField
								type="text"
								value={this.state.serverUrl}
								onChange={event => {
									this.setState({ serverUrl: event.target.value });
								}}
								id="server-field"
								label="Server URL"

								error={this.state.error}
								errorText={this.state.errorText}
							/>
						</Grid>
						<Grid item style={{ marginTop: 12 }}>
							<Button onClick={() => {
								this.setState({ serverUrl: this.props.defaultServerUrl });
							}}>
								Reset
							</Button>
						</Grid>
					</Grid>
					<Grid container item justifyContent="flex-end" direction="row" spacing={2}>
						<Grid item xs></Grid>
						<Grid item>
							<Button variant="contained" color="primary" onClick={() => {
								var newServerUrl = this.state.serverUrl.replace(/\/+$/g, '');
								verifyHost(newServerUrl, () => {
									this.setState({ errorText: undefined, error: undefined });
									this.props.onServerUrlChange(newServerUrl);
									onClose();
								}, () => {
									this.setState({ errorText: 'Invalid host.', error: true });
								});
							}}>
								Save
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</>
		);
	}

	render() {
		const settingsFormStyle = {
			width: '100%',
			maxWidth: '500px',
			background: this.props.theme.palette.background.paper,
			borderWidth: 0,
			padding: '10px 16px 16px'
		};

		return (
			<div style={{ display: 'flex', flexFlow: 'column', height: '100%', overflow: 'hidden', background: this.props.theme.palette.background.paper }}>
				<div style={{ flex: '0 1 auto', background: this.props.theme.palette.background.header, borderBottom: this.props.theme.border }}>
					<Grid container direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
						<Grid item>
							<Popup
								trigger={<Button color="primary">Settings</Button>}
								modal
								closeOnDocumentClick={false}
								closeOnEscape={false}
								contentStyle={settingsFormStyle}
								lockScroll
								onClose={() => {
									this.setState({ serverUrl: this.props.serverUrl });
								}}
								overlayStyle={{ zIndex: 1500 }}
							>
								{close => this.renderSettings(close)}
							</Popup>
						</Grid>
					</Grid>
				</div>
				{this.props.testnet && <div style={{ flex: '0 1 0' }}>
					<Paper style={{ ...info }}>
						<div style={{ float: 'left', display: 'inline-block', marginRight: 8 }}>
							<Info />
						</div>
						<div>
							This is DERIBIT TESTNET website, for the production website go
							to <Link href="https://www.deribit.com">https://www.deribit.com</Link>.
							<br/>
							Use only your testnet API Keys here.
						</div>
					</Paper>
				</div>}
				{!this.props.authenticated && (this.props.isFix || this.props.service.name === SERVICE_NAMES.main) && <div style={{ flex: '0 1 0' }}>
					<Paper style={{ ...info }}>
						<div style={{ float: 'left', display: 'inline-block', marginRight: 8 }}>
							<Info />
						</div>
						{ this.props.isFix
							? <div>
								Not authenticated. To send FIX requests use Login tab or
								execute a <code>Logon(A)</code> request.
							</div>
							: <div>
								No access token. To use private methods use Token tab or
								execute <code>/public/auth</code> request to obtain valid token.
							</div>
						}
					</Paper>
				</div>}
				{this.props.authenticated && this.state.tokenExpired && <div style={{ flex: '0 1 0' }}>
					<Paper style={{ ...info }}>
						<div style={{ float: 'left', display: 'inline-block', marginRight: 8 }}>
							<Info />
						</div>
						<div>
							Access token expired, please refresh access token <Cached style={{ marginBottom: '-4px' }} /> on Token tab.
						</div>
					</Paper>
				</div>}
				<div style={{ overflowY: 'auto', overflowX: 'hidden', flex: '1 1 auto', padding: 8, width: '100%' }}>
					{
						this.props.isFix
							? <FixRequestBuilder
								fixSpecs={this.state.fixSpecs}
								instruments={this.state.instruments}
								onSend={this.props.sendRequest}
								ref={ref => { this.requestBuilder = ref; }}
							/>
							: <RequestBuilder
								deribitApi={this.state.deribitApi}
								instruments={this.state.instruments}
								service={this.props.service}
								onSend={this.props.sendRequest}
								ref={ref => { this.requestBuilder = ref; }}
							/>}
				</div>
			</div>
		);
	}
}

RequestControl.propTypes = {
	sendRequest: PropTypes.func,
	onServerUrlChange: PropTypes.func,

	defaultServerUrl: PropTypes.string,
	platformUrl: PropTypes.string,
	serverUrl: PropTypes.string,
	service: PropTypes.shape({
		name: PropTypes.string,
		restEndpoint: PropTypes.string,
		wsEndpoint: PropTypes.string,
		apiPath: PropTypes.string,
		allowedMethods: PropTypes.arrayOf(PropTypes.string),
	}),
	fixApiPath: PropTypes.string,
	isFix: PropTypes.bool,
	testnet: PropTypes.bool,
	authenticated: PropTypes.bool,

	theme: PropTypes.object,
};

export default withTheme(RequestControl);
